import Layout from "../../components/Layout";
import { Helmet } from "react-helmet-async";
import {
  FiSettings,
  FiTool,
  FiBarChart2,
  FiCalendar,
  FiClipboard,
  FiTrendingUp,
  FiPackage,
  FiClock,
  FiCheckCircle,
} from "react-icons/fi";

const ModuleManufacturing = () => {
  const trackCTA = () => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "click", {
        event_category: "CTA",
        event_label: "Sign Up - Manufacturing Page",
      });
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Manufacturing | RedERP</title>
        <meta
          name="description"
          content="RedERP’s Manufacturing module lets you plan, manage, and track your production operations in real-time. Control costs, resources, and timelines with efficiency."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-10 relative">
          {/* Text Section */}
          <div className="flex-1 text-left mt-16">
            <h1 className="text-4xl font-bold mb-4">Manufacturing</h1>
            <p className="mb-6 text-sm sm:text-base">
              From bill of materials to final product — manage all your production processes efficiently with RedERP. Reduce waste, track performance, and optimize operations.
            </p>
            <a
              href="https://app.rederp.pro/sign-up"
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackCTA}
              className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
            >
              Start Free Trial
            </a>
          </div>

          {/* Image */}
          <div className="flex-1 hidden md:flex justify-end mt-12 mb-0">
            <img
              src="/assets/images/manufacturing1.png"
              alt="Manufacturing Illustration"
              className="w-[420px] lg:w-[420px] xl:w-[420px] h-[310px] object-contain"
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 text-left">
          {[
            {
              icon: <FiSettings size={24} />,
              title: "Production Planning",
              desc: "Create production orders, assign tasks, and schedule operations with accuracy.",
            },
            {
              icon: <FiClipboard size={24} />,
              title: "Bill of Materials (BOM)",
              desc: "Define multi-level BOMs and link raw materials, labor, and machines to final products.",
            },
            {
              icon: <FiTool size={24} />,
              title: "Work Center Management",
              desc: "Set up and track multiple workstations, machines, and human resources.",
            },
            {
              icon: <FiPackage size={24} />,
              title: "Raw Material Tracking",
              desc: "Monitor input usage and material flow through different production stages.",
            },
            {
              icon: <FiClock size={24} />,
              title: "Time & Resource Logging",
              desc: "Record cycle time, operator hours, and machine usage per production run.",
            },
            {
              icon: <FiBarChart2 size={24} />,
              title: "Production KPIs",
              desc: "Visualize production efficiency, yield loss, bottlenecks, and order delays.",
            },
            {
              icon: <FiCalendar size={24} />,
              title: "Maintenance Scheduling",
              desc: "Plan preventive maintenance and avoid costly machine downtime.",
            },
            {
              icon: <FiTrendingUp size={24} />,
              title: "Costing & Analysis",
              desc: "Calculate actual vs. planned cost of production using real-time data.",
            },
            {
              icon: <FiCheckCircle size={24} />,
              title: "Quality Control",
              desc: "Apply quality checkpoints and record inspections throughout the production cycle.",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="p-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-3 text-primary mb-3">
                {item.icon}
                <h3 className="font-bold">{item.title}</h3>
              </div>
              <p className="text-sm text-gray-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white text-center">
        <div className="max-w-xl mx-auto px-6">
          <h3 className="text-3xl font-bold mb-4">Digitize Your Production Floor</h3>
          <p className="mb-6">Start your 7-day free trial and optimize manufacturing from day one.</p>
          <a
            href="https://app.rederp.pro/sign-up"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
          >
            Try Manufacturing Module
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default ModuleManufacturing;
